import { computed, watch } from "vue";
import { just } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { defineStore } from "pinia";
import { useTea } from "../lib/tea";
import type {
  DatabaseEntrySummary,
  NoteEntry,
  UnplacedItem,
} from "../lib/outlineEntityTypes";
import { listProjectDatabaseEntries } from "../lib/tauri/database";
import { listProjectNotes } from "../lib/tauri/notes";
import { listProjectUnplaced } from "../lib/tauri/unplaced";
import { useEditorSessionStore } from "./useEditorSessionStore";
import {
  initialOutlineEntitiesModel,
  updateOutlineEntities,
  type OutlineEntitiesCmd,
  type OutlineEntitiesMsg,
} from "./useOutlineEntitiesStore.update";

/**
 * Sidecar entity lists (notes/worldbuilding entries) shown across several
 * outline tabs, kept in one store so every tab that needs one of these lists
 * (or, for Search, all of them as its corpus) reads the same live data.
 *
 * TEA: pure Model/Msg in `useOutlineEntitiesStore.update.ts`. Invokes run as
 * Cmds here and report back with `*Loaded` / `LoadFailed` Msgs. The list refs
 * stay writable (a write dispatches the matching `*Loaded` Msg).
 */
export const useOutlineEntitiesStore = defineStore("outlineEntities", () => {
  const session = useEditorSessionStore();

  async function runCmd(cmd: OutlineEntitiesCmd): Promise<void> {
    switch (cmd.kind) {
      case "loadNotes":
        try {
          const notes = await listProjectNotes(cmd.root);
          await dispatch({ type: "NotesLoaded", notes });
        } catch (error) {
          await dispatch({ type: "LoadFailed", list: "notes", error });
        }
        return;
      case "loadDatabase":
        try {
          const summaries = await listProjectDatabaseEntries(cmd.root);
          await dispatch({ type: "DatabaseLoaded", summaries });
        } catch (error) {
          await dispatch({ type: "LoadFailed", list: "database", error });
        }
        return;
      case "loadUnplaced":
        try {
          const items = await listProjectUnplaced(cmd.root);
          await dispatch({ type: "UnplacedLoaded", items });
        } catch (error) {
          await dispatch({ type: "LoadFailed", list: "unplaced", error });
        }
        return;
      case "logError":
        console.error(cmd.message, cmd.error);
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea<
    typeof initialOutlineEntitiesModel,
    OutlineEntitiesMsg,
    OutlineEntitiesCmd
  >(initialOutlineEntitiesModel, updateOutlineEntities, {
    runCmd: just(runCmd),
  });

  const notes = computed<NoteEntry[]>({
    get: () => model.value.notes,
    set: (next) => {
      void dispatch({ type: "NotesLoaded", notes: next });
    },
  });
  const databaseEntries = computed<string[]>({
    get: () => model.value.databaseEntries,
    set: (names) => {
      void dispatch({ type: "DatabaseEntryNamesSet", names });
    },
  });
  const databaseEntryFields = computed<DatabaseEntrySummary[]>({
    get: () => model.value.databaseEntryFields,
    set: (next) => {
      void dispatch({ type: "DatabaseLoaded", summaries: next });
    },
  });
  const unplaced = computed<UnplacedItem[]>({
    get: () => model.value.unplaced,
    set: (next) => {
      void dispatch({ type: "UnplacedLoaded", items: next });
    },
  });

  async function refreshNotes(): Promise<void> {
    await dispatch({ type: "RefreshNotes", root: session.rootPath });
  }

  async function refreshDatabase(): Promise<void> {
    await dispatch({ type: "RefreshDatabase", root: session.rootPath });
  }

  async function refreshUnplaced(): Promise<void> {
    await dispatch({ type: "RefreshUnplaced", root: session.rootPath });
  }

  watch(
    () => session.rootPath,
    () => {
      void refreshNotes();
      void refreshDatabase();
      void refreshUnplaced();
    },
    { immediate: true },
  );

  return {
    notes,
    databaseEntries,
    databaseEntryFields,
    unplaced,
    refreshNotes,
    refreshDatabase,
    refreshUnplaced,
  };
});
